import App from '@/App.vue';
import { Config, isProduction } from '@/config';
import { Clarity } from '@/plugins/clarity';
import closable from '@/directives/closable';
import { applyTenantTerminology, i18n } from '@/plugins/i18n.js';
import { Sentry } from '@/plugins/sentry';
import Toast from '@/plugins/toast';
import { track } from '@/plugins/track';
import { dictionary } from '@/plugins/vee-validate';
import { vuetify } from '@/plugins/vuetify';
import router from '@/router';
import { ApiService } from '@/services/api.service';
import { StorageService } from '@/services/storage.service';
import store from '@/store/store';
import { startingLocale } from '@/util/i18n/get-starting-locale';
import { localize } from '@/util/i18n/localize';
import 'scroll-behavior-polyfill';
import { VClosePopover, VPopover, VTooltip } from 'v-tooltip';
import Validator from 'vee-validate';
import Vue from 'vue';
import vueDebounce from 'vue-debounce';
import VueScrollTo from 'vue-scrollto';
import underscore from 'vue-underscore';
import UUID from 'vue-uuid';
import VueJWT from 'vuejs-jwt';
import VueApexCharts from 'vue-apexcharts';
import sortable from '@/directives/sortable';
import 'mapbox-gl/dist/mapbox-gl.css';
import withRetry from '@/plugins/with-retry';
import { reportBootRecovery } from '@/util/boot-recovery';

// Proof for the boot-recovery watchdog in public/index.html that the entry bundle ran.
window.__ALOHA_BOOTED__ = true;

Sentry.init();
reportBootRecovery();
Clarity.init();

Vue.config.productionTip = false;
Vue.use(
  track,
  {
    config: {
      id: Config.gtag,
    },
    appName: 'Aloha',
    pageTrackerScreenviewEnabled: true,
    enabled: isProduction(),
    // vue-gtag loads gtag.js whenever bootstrap is on, whatever `enabled` says,
    // so a build without an id (the desktop app's, the local stack's) would
    // request gtag.js?id=. Production sets VUE_APP_GTAG, so it is unchanged.
    bootstrap: Boolean(Config.gtag),
  },
  router,
);

Vue.use(underscore);
Vue.use(UUID);
Vue.use(Validator, { dictionary });
Vue.use(VueScrollTo);
Vue.use(VueJWT);
Vue.use(vueDebounce, { listenTo: 'input' });
Vue.use(Toast);
Vue.use(withRetry);
Vue.use(VueApexCharts);
Vue.component('v-popover', VPopover);
Vue.component('apexchart', VueApexCharts);
Vue.directive('closable', closable);
Vue.directive('tooltip', VTooltip);
Vue.directive('close-popover', VClosePopover);
Vue.directive('sortable', sortable);

const enCharts = require('@/locales/en-CA-charts.json');
const frCharts = require('@/locales/fr-CA-charts.json');
const locales = [frCharts.charts.options, enCharts.charts.options];

if (typeof Apex === 'undefined') {
  window.Apex = {};
}
Apex.chart = {
  locales: locales,
  defaultLocale: startingLocale,
};

ApiService.init();

// Localize with the starting locale
localize(startingLocale);

// If token exists set header
if (StorageService.getToken()) {
  ApiService.setHeader();
}

export { bus } from '@/event-bus';

const app = new Vue({
  i18n,
  vuetify,
  router,
  store,
  created() {
    // Init current index page HTML
    store.dispatch('appVersionDetector/initCurrentHtml');
  },
  render: (h) => h(App),
});

// Tenant terminology overlay: the real per-tenant overlay is fetched and applied once after auth
// by the `terminology/load` action (dispatched from auth/validate; see store/terminology.module.js).
// In development, the dev seam lets you preview a rename via `?tenantTerms=lead` or
// `window.__setTenantTerms(...)` — and that dev override takes precedence over the backend fetch.
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  require('@/util/i18n/terminology/dev-tenant-overlay').installDevTenantOverlay(applyTenantTerminology);
}

if (process.env.NODE_ENV !== 'test') {
  app.$mount('#app');
}
